import { formatSEK, isIncomingSwish } from '@budgie/shared';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '../../lib/trpc';
import { TableSkeleton } from '../common/Skeleton';
import { TransactionCategoryCell } from '../transactions/TransactionCategoryCell';

type Props = {
  categoryId: string;
  categoryName: string;
  onClose: () => void;
};

const PAGE_SIZE = 50;

export function CategoryTransactionsDialog({ categoryId, categoryName, onClose }: Props) {
  const [offset, setOffset] = useState(0);
  const query = trpc.category.transactions.useQuery(
    { categoryId, limit: PAGE_SIZE, offset },
    { retry: false },
  );

  const transactions = query.data?.transactions ?? [];
  const hasMore = transactions.length === PAGE_SIZE;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex max-h-[85dvh] flex-col overflow-hidden sm:max-h-[85vh] sm:max-w-4xl"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>Transaktioner – {categoryName}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {query.isLoading ? (
            <TableSkeleton rows={8} columns={4} />
          ) : query.error ? (
            <p className="text-sm text-destructive">{query.error.message}</p>
          ) : transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Inga transaktioner i denna kategori.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Beskrivning</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead>Konto</TableHead>
                    <TableHead className="text-right">Belopp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell className="tabular-nums text-xs text-muted-foreground">
                        {txn.date}
                      </TableCell>
                      <TableCell className="max-w-[250px] truncate">{txn.description}</TableCell>
                      <TableCell>
                        <TransactionCategoryCell
                          transactionId={txn.id}
                          categoryId={categoryId}
                          categorySource={txn.categorySource}
                          ruleAllowed={!isIncomingSwish(txn.description, txn.amount)}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {txn.accountName ?? 'Okänt konto'}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums ${txn.amount >= 0 ? 'text-positive' : 'text-foreground'}`}
                      >
                        {formatSEK(txn.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {!query.isLoading && transactions.length > 0 && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Föregående sida"
            >
              <span aria-hidden="true">←</span> Föregående
            </Button>
            <span className="text-xs text-muted-foreground">
              {offset + 1}–{offset + transactions.length}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasMore}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Nästa sida"
            >
              Nästa <span aria-hidden="true">→</span>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
